import { format } from 'date-fns';
import { T, Num, Var } from 'gt-react';
import type { MasterlistSummary } from '@erp/rpc';

const UPLOADED_AT_FORMAT = 'MMM d, yyyy HH:mm';

/** When the workbook was uploaded, and by whom where we can resolve a name. */
function UploadedLine({ masterlist }: { masterlist: MasterlistSummary }) {
  const at = format(new Date(masterlist.uploadedAt), UPLOADED_AT_FORMAT);

  if (!masterlist.uploadedBy) {
    return (
      <T context="PCSC Masterlist page, upload provenance">
        Uploaded on <Var>{at}</Var>
      </T>
    );
  }
  return (
    <T context="PCSC Masterlist page, upload provenance">
      Uploaded by <Var>{masterlist.uploadedBy}</Var> on <Var>{at}</Var>
    </T>
  );
}

/**
 * What this workbook could not be read for.
 *
 * Shown because the upload succeeded anyway: a vendor whose tabs changed shape
 * still contributes its price classes, so the file loads, and the only thing
 * standing between that and a silent half-loaded masterlist is saying so here.
 */
function ReadQuality({ masterlist }: { masterlist: MasterlistSummary }) {
  if (
    masterlist.degradedVendors.length === 0 &&
    masterlist.conflicts.length === 0 &&
    masterlist.malformedRows.length === 0
  ) {
    return null;
  }

  return (
    <div className="border-destructive/40 bg-destructive/5 mt-3 space-y-1 rounded border p-3">
      {masterlist.degradedVendors.length > 0 ? (
        <p className="text-destructive text-sm font-medium">
          <T context="PCSC Masterlist page, degraded vendor warning">
            No hierarchy codes were read for{' '}
            <Var>{masterlist.degradedVendors.join(', ')}</Var>. That vendor's
            codes will not resolve until a workbook covering their tabs replaces
            this one.
          </T>
        </p>
      ) : null}
      {masterlist.malformedRows.length > 0 ? (
        <p className="text-muted-foreground text-sm">
          <T context="PCSC Masterlist page, malformed rows warning">
            <Num>{masterlist.malformedRows.length}</Num> row(s) were skipped
            because their price class is not a price class:{' '}
            <Var>{masterlist.malformedRows.slice(0, 3).join('; ')}</Var>
          </T>
        </p>
      ) : null}
      {masterlist.conflicts.length > 0 ? (
        <p className="text-muted-foreground text-sm">
          <T context="PCSC Masterlist page, contradictory codes warning">
            <Num>{masterlist.conflicts.length}</Num> code(s) were stated twice
            with different price classes:{' '}
            <Var>{masterlist.conflicts.slice(0, 5).join(', ')}</Var>
          </T>
        </p>
      ) : null}
    </div>
  );
}

/**
 * What crossing is in force, or that none is.
 *
 * Counts rather than rows: the workbook states thousands of crossings and this
 * panel's job is to say what is loaded, not to list it.
 */
export function MasterlistStatus({
  masterlist,
  isLoading,
  isError,
}: {
  masterlist: MasterlistSummary | null;
  isLoading: boolean;
  /**
   * Shown apart from the empty state on purpose: a failed read and an empty
   * one look identical from here, and telling a specialist nothing is loaded
   * when we simply could not ask sends them to re-upload a workbook that is
   * already in force.
   */
  isError: boolean;
}) {
  if (isLoading) {
    return (
      <p className="text-muted-foreground text-sm">
        <T context="PCSC Masterlist page, loading state">Loading…</T>
      </p>
    );
  }

  if (isError) {
    return (
      <p className="text-destructive text-sm">
        <T context="PCSC Masterlist page, load failure">
          Could not read what is currently loaded. This is not the same as
          nothing being loaded — try again before re-uploading.
        </T>
      </p>
    );
  }

  if (!masterlist) {
    return (
      <p className="text-muted-foreground text-sm">
        <T context="PCSC Masterlist page, empty state">
          No masterlist is loaded yet. Upload the PCSC Masterlist workbook to
          start resolving vendor codes.
        </T>
      </p>
    );
  }

  return (
    <div className="space-y-1">
      <p className="font-medium">
        <Var>{masterlist.fileName}</Var>
      </p>
      <p className="text-muted-foreground text-sm">
        <T context="PCSC Masterlist page, what is loaded">
          <Num>{masterlist.rowCount}</Num> crossings across{' '}
          <Num>{masterlist.tabCount}</Num> tabs, covering{' '}
          <Num>{masterlist.vendorCount}</Num> vendors.
        </T>
      </p>
      <p className="text-muted-foreground text-sm">
        <UploadedLine masterlist={masterlist} />
      </p>
      <ReadQuality masterlist={masterlist} />
    </div>
  );
}
